/**
 * Hook for managing step navigation in the reflection flow
 * Handles step transitions, URL synchronization, and navigation logic
 */

import { useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { REFLECTION_STEPS, type ReflectionStep } from "../constants";

interface UseStepNavigationProps {
	currentStep: ReflectionStep;
	setCurrentStep: (step: ReflectionStep) => void;
	reflectionId: string | null;
}

export function useStepNavigation({
	currentStep,
	setCurrentStep,
	reflectionId,
}: UseStepNavigationProps) {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const isNavigatingBackRef = useRef(false);

	/**
	 * Get the index of a step in the flow
	 */
	const getStepIndex = useCallback((step: ReflectionStep): number => {
		return REFLECTION_STEPS.indexOf(step);
	}, []);

	/**
	 * Navigate to a specific step
	 */
	const navigateToStep = useCallback(
		(step: ReflectionStep, updateUrl = true) => {
			setCurrentStep(step);

			if (updateUrl) {
				const params = new URLSearchParams(searchParams);
				params.set("step", step);

				if (reflectionId) {
					params.set("id", reflectionId);
				}

				navigate(`/reflection-flow?${params.toString()}`, { replace: true });
			}
		},
		[setCurrentStep, searchParams, navigate, reflectionId],
	);

	/**
	 * Navigate to the next step
	 */
	const goNext = useCallback(() => {
		const currentIndex = getStepIndex(currentStep);
		if (currentIndex < REFLECTION_STEPS.length - 1) {
			const nextStep = REFLECTION_STEPS[currentIndex + 1];
			navigateToStep(nextStep);
		}
	}, [currentStep, getStepIndex, navigateToStep]);

	/**
	 * Navigate to the previous step
	 */
	const goBack = useCallback(() => {
		isNavigatingBackRef.current = true;
		const currentIndex = getStepIndex(currentStep);
		if (currentIndex > 0) {
			const prevStep = REFLECTION_STEPS[currentIndex - 1];
			navigateToStep(prevStep);
		}
		// Reset flag after a short delay
		setTimeout(() => {
			isNavigatingBackRef.current = false;
		}, 100);
	}, [currentStep, getStepIndex, navigateToStep]);

	/**
	 * Check if we can go to the next step
	 */
	const canGoNext = useCallback((): boolean => {
		const currentIndex = getStepIndex(currentStep);
		return currentIndex < REFLECTION_STEPS.length - 1;
	}, [currentStep, getStepIndex]);

	/**
	 * Check if we can go to the previous step
	 */
	const canGoBack = useCallback((): boolean => {
		const currentIndex = getStepIndex(currentStep);
		return currentIndex > 0;
	}, [currentStep, getStepIndex]);

	/**
	 * Check if we're navigating back (to prevent URL sync interference)
	 */
	const isNavigatingBack = useCallback((): boolean => {
		return isNavigatingBackRef.current;
	}, []);

	return {
		navigateToStep,
		goNext,
		goBack,
		canGoNext,
		canGoBack,
		isNavigatingBack,
		getStepIndex,
	};
}
